
// ------------------------------------------------------------
//  handleVideoCueFromAST(ast, cueElement)
// ------------------------------------------------------------
// Plays a video from window.videoDir according to cue:video(...) params.
// Supported params:
// file:, size:, loop:, hold:, speed:, offsetX:, offsetY:, location:(fixed|scroll),
// target:(uid), in:, out:, fadeIn:, fadeOut:, opacity:, audio:(0|1), closeBtn:(0|1),
// backdrop:(0|1), fit:(contain|cover|fill) — how the video fills its box:
//   contain (default) letterboxes, cover scales+crops to fill, fill stretches
//
// fadeIn:, fadeOut: and hold: are beats of tempo(); in:, out: and seg: are
// places in the recording, in its own seconds.
//
// Example (fullscreen with close button):
//   video(file:myvideo.mp4, size:fullscreen, closeBtn:1, fadeIn:0.3, fadeOut:0.3)
//
// From a line of the live console, as image() and shape() are placed:
//   video(uid:v, file:"https://…/clip.mp4", at:[0.75, 0.3], size:0.3)
//     at: the middle, 0..1 of the window; size: up to 1 a share of it
//   video(uid:v, remove:1)  /  video(remove:all)

import { registerEscapeLayer } from "../system/keyLayers.js";
import { makePatternGenerator, evalMaybeRandom } from "./audio/audioShared.js";
import { bindParam, releaseBinding, isSignalRef, isOscRef, publish } from "../control/paramBinding.js";
import { secs } from "./beats.js";
import { atPair } from "../system/viewBand.js";
import { onReset } from "../system/teardown.js";
import { mapHtml, rebend, forgetHtml } from "../system/mapHandles.js";
import { mapText } from "../system/mapWarp.js";
import { findFile, urlOf, missNote } from "../system/mediaFolder.js";

// A project switch takes every video, each its own way out: its backdrop,
// close button, follow loop and Escape layer go with it
onReset("project", function removeAllVideos() {
  document.querySelectorAll(".cue-video").forEach(vid => {
    if (vid._removeCueVideo) vid._removeCueVideo(); else vid.remove();
    vid.src = "";
  });
});

// in: / seg: / speed: may be a number, a list, or a Pseq/Prand pattern —
// the same forms as audio(). A list or pattern is drawn PER GRAIN on the
// single <video> element (a seek, never a re-create): in:Prand(...) +
// seg:0.4 + loop:N is a glitch-video generator. Seek latency is set by the
// file's keyframe spacing — use an all-intra encode for fast grains.
function makeSpec(raw, fallback) {
  if (raw === undefined || raw === null || raw === "") return { gen: null, first: fallback };
  let gen = null;
  if (typeof raw === "object" && raw.type === "pattern") gen = makePatternGenerator(raw);
  else if (Array.isArray(raw)) gen = makePatternGenerator({ values: raw });
  if (gen) {
    const v = Number(evalMaybeRandom(gen.next()));
    return { gen, first: Number.isFinite(v) ? v : fallback };
  }
  const v = Number(evalMaybeRandom(raw));
  return { gen: null, first: Number.isFinite(v) ? v : fallback };
}
function drawNext(spec, current) {
  if (!spec.gen) return current;
  const v = Number(evalMaybeRandom(spec.gen.next()));
  return Number.isFinite(v) ? v : current;
}

/**
 * Resolve video path: a URL or a path from the site's root as given, else a
 * folder picked on this computer (mediaFolder.js), the project's video/, then
 * the shared one -- only the shared one with no project (Oscilla Live). Logs a
 * loud, actionable error when the file is in none of them -- a missing video
 * otherwise fails silently (black hole).
 */
async function resolveVideoPath(fileName) {
  if (/^(https?:)?\/\//.test(fileName) || fileName.startsWith("/")) return fileName;
  const local = findFile(fileName, "video");
  if (local) return urlOf(local);
  const sharedPath = `${window.sharedDir || "shared/"}video/${fileName}`;
  const places = window.videoDir ? [`${window.videoDir}${fileName}`, sharedPath] : [sharedPath];
  for (const url of places) {
    try {
      if ((await fetch(url, { method: "HEAD" })).ok) return url;
    } catch { /* the next */ }
  }
  console.error(
    `[cueVideo] ❌ "${fileName}" not found — looked in ${places.join(" and ")}. ` +
    (window.videoDir
      ? "Check the exact spelling/case of the filename, or use the DSL editor's Browse… button on the video file field to copy it into the project's video/ folder."
      : "With no project, give a URL (file:\"https://…/clip.mp4\")") +
    missNote(fileName, "video")
  );
  return places[0]; // still set it so the <video> element reports the 404 too
}

// Active cue videos, newest last — Escape dismisses the most recent one.
// This is the only manual dismissal for fullscreen videos without
// clickable:1 (their pointer-events are disabled so clicks pass through).
const _activeCueVideos = [];
let _escDismissBound = false;
function _bindEscDismiss() {
  if (_escDismissBound) return;
  _escDismissBound = true;
  // A fullscreen video sits above everything, so it outranks every other
  // Escape layer — see js/system/keyboardShortcuts.js.
  registerEscapeLayer({
    id: "cue-video-fullscreen",
    tier: "overlay",
    isActive: () => _activeCueVideos.length > 0,
    dismiss: () => _activeCueVideos[_activeCueVideos.length - 1](),
  });
}

/**
 * at: and size: as a live line gives them, placed as image() and shape()
 * are: at:[x, y] the middle, 0..1 of the window; size: up to 1 a share of
 * it, across and down. True when the line asked for either.
 */
function placeOnView(vid, p) {
  const at = atPair(p.at);
  const xy = Array.isArray(at) ? at.map(v => Number(evalMaybeRandom(v))) : null;
  const share = typeof p.size === "number" && p.size > 0 && p.size <= 1 ? p.size : null;
  const placed = xy?.length === 2 && xy.every(Number.isFinite);
  if (!placed && share == null) return false;
  const w = share != null ? share * innerWidth : vid.width || 320;
  const h = share != null ? share * innerHeight : vid.height || 180;
  const [x, y] = placed ? xy : [0.5, 0.5];
  vid.width = Math.round(w);
  vid.height = Math.round(h);
  Object.assign(vid.style, {
    position: "fixed", transform: "",
    left: `${x * innerWidth - w / 2}px`, top: `${y * innerHeight - h / 2}px`,
    width: `${w}px`, height: `${h}px`,
  });
  return true;
}

/** map: on a film: the <video> itself bent (mapHandles.js), where it is shown unbent where map:1's corners start. */
function videoMap(vid, v) {
  const uid = vid.dataset.uid;
  mapHtml(`video:${uid}`, v, {
    flat: () => vid.getBoundingClientRect(),
    targets: () => (vid.isConnected ? [{ el: vid }] : []),
    line: pts => `video(uid:${uid}, map:${mapText(pts)})`,
    write: { uid, cues: ["video"] },
  });
}

export async function handleVideoCueFromAST(ast, cueElement = null) {
  if (!ast?.params) return console.error("[cueVideo] Missing AST params.");

  const p = ast.params;
  // map:[...] alone -- a fit dragged on the laptop, run by the projector: only that
  if (p.map != null && p.uid != null && Object.keys(p).every(k => k === "uid" || k === "map")) {
    const v = document.querySelector(`video[data-uid="${CSS.escape(String(p.uid))}"]`);
    if (v) videoMap(v, p.map);
    return;
  }
  // video(uid:v, remove:1): that one taken away; remove:all every one (as shape())
  if (p.remove != null && p.remove !== 0 && p.remove !== "0") {
    const all = String(p.remove).toLowerCase() === "all";
    for (const v of document.querySelectorAll("video[data-uid]")) {
      if (all || v.dataset.uid === String(p.uid)) v._removeCueVideo?.();
    }
    return;
  }
  // src: accepted as an alias for file:, since audio cues use src:
  const rawFile = p.file ?? p.src;
  if (!rawFile) return console.error("[cueVideo] Missing required 'file' parameter (or src:).");
  if (!p.file) p.file = rawFile;

  // Start point / grain length / rate — number, list, or pattern (drawn per grain)
  const inSpec = makeSpec(p.in, 0);
  const segSpec = makeSpec(p.seg, 0);
  const speedSpec = makeSpec(p.speed, 1);
  const grainMode = segSpec.first > 0 || !!inSpec.gen || !!speedSpec.gen;

  // --- Build source path (project first, shared fallback)
  let fileName = String(rawFile).trim();
  // case-insensitive: "clip.MP4" must not become "clip.MP4.mp4"
  if (!fileName.match(/\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i)) fileName += ".mp4";
  const src = await resolveVideoPath(fileName);

  // --- Unique key and instance control
  // Retriggering a cue RESTARTS its existing video instead of stacking a
  // new copy on top. A uid identifies the instance (and is matched first);
  // otherwise file+target is the identity. Only new:1 opts into stacking.
  const key = `${p.file}_${p.target || "none"}`;
  const allowNewInstance = p.new === "1" || p.new === 1;

  // a uid is the instance: another uid on the same file is another video
  const existing = allowNewInstance ? null
    : p.uid ? document.querySelector(`video[data-uid="${CSS.escape(String(p.uid))}"]`)
    : document.querySelector(`video[data-key="${CSS.escape(key)}"]`);

  if (existing) {
    existing.currentTime = inSpec.first;
    existing.playbackRate = Math.abs(speedSpec.first) || 1;
    // sound: is audio: by another name
    const hasAudio = p.audio ?? p.sound;
    existing.muted = !(hasAudio === "1" || hasAudio === 1 || hasAudio === "true" || hasAudio === true);
    // a fade that has run holds its last opacity (fill: forwards) over the
    // style, and the first run's out: fade would fade the restart again
    clearTimeout(existing._outFade);
    for (const a of existing.getAnimations()) a.cancel();
    existing.style.opacity = p.opacity ? Number(p.opacity) : 1;
    placeOnView(existing, p);                 // a live line run again with another at: / size:
    if (p.map != null) videoMap(existing, p.map);
    else rebend(`video:${existing.dataset.uid}`);
    existing.play();
    return;
  }

  // --- Create video element
  const vid = document.createElement("video");
  vid.classList.add("cue-video");

  const uid = p.uid || `video-${Date.now()}-${Math.floor(Math.random() * 9999)}`;
  vid.id = uid;
  vid.dataset.uid = uid;
  vid.dataset.key = key;
  vid.src = src;
  vid.autoplay = true;
  vid.playsInline = true;
  // sound: is audio: by another name
  const hasAudioNew = p.audio ?? p.sound;
  vid.muted = !(hasAudioNew === "1" || hasAudioNew === 1 || hasAudioNew === "true" || hasAudioNew === true);
  vid.controls = (p.controls === 1 || p.controls === "1" || p.controls === true);
  vid.loop = false;
  vid.playbackRate = Math.abs(speedSpec.first) || 1;   // browsers can't play <video> backwards
  vid.style.position = p.location === "fixed" ? "fixed" : "absolute";
  vid.style.cursor = "pointer";
  // layer:back puts the video BEHIND the score: inserted before the score
  // content in the DOM with no z-index, so SVG elements paint above it.
  // (Score backgrounds must be transparent over the video area — an opaque
  // background rect in the SVG will hide a background video.)
  const layerBack = ["back", "behind", "background"].includes(String(p.layer || "").toLowerCase());
  if (layerBack) {
    vid.style.pointerEvents = "none"; // never swallow score clicks
  } else {
    vid.style.zIndex = 9999;  // Above backdrop (9998), below close button (10000)
  }
  vid.style.border = "none";
  vid.style.opacity = p.fadeIn ? 0 : p.opacity ? Number(p.opacity) : 1;
  vid.style.transition = "opacity 0.5s ease";

  // ── where it has got to, as a signal ────────────────────────────────────
  // requestVideoFrameCallback reports once per decoded frame, which is what a
  // control signal wants; timeupdate is the fallback and fires about four
  // times a second, fine for following but visibly stepped. Either way the
  // publish() throttle caps it at one message per 16 ms.
  const reportTime = () => {
    const d = vid.duration;
    publish("video", uid, {
      time: vid.currentTime,
      t: Number.isFinite(d) && d > 0 ? vid.currentTime / d : 0,
    });
  };
  // timeupdate ALWAYS, rVFC as well when it exists. rVFC is per-frame and
  // smooth, but it is driven by the compositor: in a background tab it stops
  // firing while the video keeps playing, which would freeze a bound parameter
  // at its last value rather than following. timeupdate is only ~4 Hz but it
  // keeps running, so it is the floor. Reporting twice costs nothing — publish()
  // throttles to one message per 16 ms either way.
  vid.addEventListener("timeupdate", reportTime);
  if (typeof vid.requestVideoFrameCallback === "function") {
    const onFrame = () => {
      if (!vid.isConnected) return;         // removed: stop asking for frames
      reportTime();
      vid.requestVideoFrameCallback(onFrame);
    };
    vid.requestVideoFrameCallback(onFrame);
  }

  // fit: how the video fills its box (fullscreen or w/h) when aspect
  // ratios differ — contain letterboxes (default), cover crops, fill
  // stretches. Also accepts stretch as an alias for fill.
  const fit = String(p.fit || "").toLowerCase();
  if (fit === "cover") vid.style.objectFit = "cover";
  else if (fit === "fill" || fit === "stretch") vid.style.objectFit = "fill";
  else vid.style.objectFit = "contain";


  // --- Geometry base
  const score = document.getElementById("scoreContainer");
  const stage = document.getElementById("scrollStage");
  const containerBox = score?.getBoundingClientRect?.() || { left: 0, top: 0 };

  const targetEl = p.target ? document.getElementById(p.target) : null;
  const baseEl = targetEl || cueElement;
  const offsetX = Number(p.offsetX) || 0;
  const offsetY = Number(p.offsetY) || 0;

  // Target centre in the space the video is positioned in: stage-local for
  // location:scroll (the video is appended into #scrollStage below, so it
  // rides scroll and zoom), viewport for fixed. Read once here for the
  // initial placement, and every frame by follow:1 further down.
  function anchorPoint() {
    if (!baseEl?.getBoundingClientRect) return null;
    const bbox = baseEl.getBoundingClientRect();
    const centerX = bbox.left + bbox.width / 2;
    const centerY = bbox.top + bbox.height / 2;

    if (p.location === "scroll") {
      // The score scrolls via a CSS transform on #scrollStage, not
      // scrollLeft — position in stage-local coordinates.
      const stageBox = stage?.getBoundingClientRect?.();
      if (stageBox && stage.offsetWidth) {
        const sx = stageBox.width / stage.offsetWidth || 1;
        const sy = stageBox.height / stage.offsetHeight || 1;
        return { x: (centerX - stageBox.left) / sx + offsetX,
                 y: (centerY - stageBox.top) / sy + offsetY };
      }
      // fallback: scrollLeft-based containers
      return { x: centerX - containerBox.left + (score?.scrollLeft || 0) + offsetX,
               y: centerY - containerBox.top + (score?.scrollTop || 0) + offsetY };
    }
    // fixed to viewport, preserve sign of offsets
    return { x: centerX + offsetX, y: centerY + offsetY };
  }

  let x = 100, y = 100;
  const anchor0 = anchorPoint();
  if (anchor0) ({ x, y } = anchor0);

  // --- Size and positioning
  let vidW = 320, vidH = 180;
  const size = (typeof p.size === "string") ? p.size.toLowerCase() : null;
  const isFsSize = size === "fs" || size === "fullscreen";
  const centerOnViewport = p.center === "1" || p.center === 1 || p.center === true;
  // Scroll videos live inside the transformed #scrollStage so they ride the
  // score's scroll; their w/h are then treated as SCORE UNITS and scaled by
  // the SVG content scale, so a video matches a same-sized score frame at
  // any zoom.
  const stageParent = !!(p.location === "scroll" && stage && !centerOnViewport && !isFsSize);
  let contentScale = 1;
  if (stageParent) {
    const svgEl = document.getElementById("score");
    const ctm = svgEl?.getScreenCTM?.();
    const stageBox = stage.getBoundingClientRect();
    const stScale = stage.offsetWidth ? (stageBox.width / stage.offsetWidth || 1) : 1;
    if (ctm && ctm.a) contentScale = ctm.a / stScale;
  }

  // Support separate w: and h: parameters (preferred)
  if (p.w) vidW = Number(p.w) || vidW;
  if (p.h) vidH = Number(p.h) || vidH;

  if (size === "fs" || size === "fullscreen") {
    Object.assign(vid.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "100vw",
      height: "100vh",
      margin: "0",
      padding: "0",
      pointerEvents: "none",
    });
    // Re-enable click handling only if explicitly requested
    if (p.clickable === "1" || p.clickable === 1) vid.style.pointerEvents = "auto";
  } else if (typeof p.size === "string" && p.size.includes("x")) {
    const [w, h] = p.size.split("x").map(Number);
    if (!isNaN(w)) vidW = w;
    if (!isNaN(h)) vidH = h;
  } else if (typeof p.size === "number" && p.size > 1) {
    vidW = p.size;
  }

  // Only set pixel dimensions for non-fullscreen; fs already has 100vw/100vh
  // from the Object.assign above and must not be overwritten.
  const dispW = stageParent ? vidW * contentScale : vidW;
  const dispH = stageParent ? vidH * contentScale : vidH;
  if (!isFsSize) {
    vid.width = Math.round(dispW);
    vid.height = Math.round(dispH);
    vid.style.width = `${dispW}px`;
    vid.style.height = `${dispH}px`;
  }

  // Position video: center on viewport if center:1 or location:fixed
  if (!isFsSize) {
    if (centerOnViewport || p.location === "fixed") {
      // Center on viewport with explicit transform for reliability
      vid.style.left = "50%";
      vid.style.top = "50%";
      vid.style.transform = `translate(-50%, -50%)`;
    } else {
      vid.style.left = `${x - dispW / 2}px`;
      vid.style.top = `${y - dispH / 2}px`;
    }
  }

  // a live line's at: / size: (0..1 of the window) over the above
  if (!isFsSize) placeOnView(vid, p);

  // --- Backdrop (optional dark overlay behind modal videos)
  let backdrop = null;
  const showBackdrop = p.backdrop === "1" || p.backdrop === 1 || p.backdrop === true;
  if (showBackdrop) {
    backdrop = document.createElement("div");
    backdrop.className = "cue-video-backdrop";
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.8);
      z-index: 9998;
    `;
  }

  // --- Append
  // For fixed position (modal), append directly to body to avoid stacking context issues
  const container = document.getElementById("videoLayer") || document.body;
  const modalContainer = (p.location === "fixed") ? document.body : container;
  if (backdrop) modalContainer.appendChild(backdrop);
  if (layerBack) {
    // Insert BEFORE the score content so later siblings (the SVG) paint on
    // top by DOM order. Fixed/fullscreen back-videos go inside the score
    // container (untransformed, so position:fixed still works) rather than
    // body, so they sit above the page background but below the score.
    if (stageParent) {
      stage.insertBefore(vid, stage.firstChild);
    } else if (p.location === "scroll" && score) {
      score.insertBefore(vid, score.firstChild);
    } else {
      const sc = document.getElementById("scoreContainer") || document.body;
      sc.insertBefore(vid, sc.firstChild);
    }
  } else if (stageParent) {
    // Inside the transformed stage the video scrolls with the score
    stage.appendChild(vid);
  } else if (p.location === "scroll" && score) {
    score.appendChild(vid);
  } else {
    modalContainer.appendChild(vid);
  }
  // map: -- bent onto a surface
  if (p.map != null) videoMap(vid, p.map);


  // --- Close button (for windowed or fullscreen videos with closeBtn:1)
  let closeBtn = null;
  const closeBtnOff = p.closeBtn === "0" || p.closeBtn === 0 || p.closeBtn === false;
  // Fullscreen videos default to showing the close button — it is the only
  // touch-device dismissal for pass-through (non-clickable) fullscreen video.
  // Disable explicitly with closeBtn:0 for performances.
  const showCloseBtn = (p.closeBtn === "1" || p.closeBtn === 1 || p.closeBtn === true) ||
                       (isFsSize && !closeBtnOff);
  if (showCloseBtn) {
    closeBtn = document.createElement("button");
    closeBtn.className = "cue-video-close";
    closeBtn.innerHTML = "✕";
    closeBtn.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      width: 44px;
      height: 44px;
      border: none;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      font-size: 24px;
      cursor: pointer;
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s;
    `;
    closeBtn.addEventListener("mouseenter", () => closeBtn.style.background = "rgba(255, 0, 0, 0.8)");
    closeBtn.addEventListener("mouseleave", () => closeBtn.style.background = "rgba(0, 0, 0, 0.7)");
    // On auto-shown fullscreen buttons, fade to a faint glyph after a few
    // seconds so it doesn't disturb the image — it stays fully tappable.
    if (isFsSize && !(p.closeBtn === "1" || p.closeBtn === 1 || p.closeBtn === true)) {
      closeBtn.style.transition = "background 0.2s, opacity 0.6s";
      setTimeout(() => { closeBtn.style.opacity = "0.25"; }, 3000);
      const wake = () => { closeBtn.style.opacity = "1"; };
      closeBtn.addEventListener("mouseenter", wake);
      closeBtn.addEventListener("touchstart", wake, { passive: true });
    }
    modalContainer.appendChild(closeBtn);
  }

  // --- Timing & fade parameters
  const fadeInDur = Number(secs(p.fadeIn || 0));     // beats -> seconds
  const fadeOutDur = Number(secs(p.fadeOut || 0));
  let inTime = inSpec.first;
  const outTime = Number(p.out || 0);
  const opacityTarget = Number(p.opacity || 1);
  const totalHold = p.hold ? Number(secs(p.hold)) * 1000 : null;

  let loopCount = 0;
  const maxLoops = Number(p.loop) || 1;
  const loopInfinite = p.loop === 0 || p.loop === "0";

  // --- Grain state (grainMode): each grain seeks to the next start point,
  // runs seg seconds at its own rate, then the next grain is drawn.
  let seg = segSpec.first;
  let rate = Math.abs(speedSpec.first) || 1;
  let grains = 0;

  // ── opacity: and speed: as inlets ───────────────────────────────────────
  // A bound opacity OWNS the value: fadeIn/fadeOut are a timed ramp on the
  // same property, and two things writing one style would fight. Same rule as
  // image()'s index: versus dur:.
  // video builds a params object, not an args list — see parser.js.
  const _bindableArg = (name) => {
    const v = ast?.params?.[name];
    return (isSignalRef(v) || isOscRef(v)) ? v : null;
  };
  const opacityArg = _bindableArg("opacity");
  const speedArg = _bindableArg("speed");

  if (opacityArg) {
    vid.style.transition = "";                 // no ramp to fight the signal
    bindParam(opacityArg, (v) => { vid.style.opacity = String(v); },
      { min: 0, max: 1, owner: `video:${uid}:opacity` });
  }
  if (speedArg) {
    // Browsers reject a rate of 0 or a very large one, and cannot play a
    // <video> backwards at all — so this is clamped to what actually works
    // rather than passed through to throw.
    bindParam(speedArg, (v) => {
      const r = Math.min(16, Math.max(0.0625, Math.abs(Number(v) || 1)));
      rate = r;
      try { vid.playbackRate = r; } catch { /* mid-seek: the next one lands */ }
    }, { min: 0.25, max: 4, owner: `video:${uid}:speed` });
  }

  // --- Cleanup
  function removeVideo() {
    forgetHtml(`video:${uid}`);
    const idx = _activeCueVideos.indexOf(removeVideo);
    if (idx !== -1) _activeCueVideos.splice(idx, 1);
    clearTimeout(vid._grainTimer);
    vid._followStop?.();
    // a follow() of its opacity or speed stops writing to it
    releaseBinding(`video:${uid}:opacity`);
    releaseBinding(`video:${uid}:speed`);
    try {
      vid.pause();
      if (vid._scrollHandler && score)
        score.removeEventListener("scroll", vid._scrollHandler);
      vid.remove();
      if (closeBtn) closeBtn.remove();
      if (backdrop) backdrop.remove();
    } catch (err) {
      console.warn("[cueVideo] Error removing video:", err);
    }
  }
  _bindEscDismiss();
  _activeCueVideos.push(removeVideo);
  vid._removeCueVideo = removeVideo;          // video(uid:v, remove:1)

  // Wire close button and backdrop click
  if (closeBtn) closeBtn.addEventListener("click", removeVideo);
  if (backdrop) backdrop.addEventListener("click", removeVideo);

  // --- Scroll tracking (non-fullscreen)
  // Stage-parented videos ride the scroll transform — no listener needed.
  if (!stageParent && p.location === "scroll" && baseEl && score && size !== "fs" && size !== "fullscreen") {
    const updatePos = () => {
      const bbox = baseEl.getBoundingClientRect();
      const sx = score.scrollLeft || 0;
      const sy = score.scrollTop || 0;
      vid.style.left = `${bbox.left - containerBox.left + sx + bbox.width / 2 - vidW / 2 + offsetX}px`;
      vid.style.top = `${bbox.top - containerBox.top + sy + bbox.height / 2 - vidH / 2 + offsetY}px`;
    };
    vid._scrollHandler = updatePos;
    score.addEventListener("scroll", updatePos);
  }

  // --- Follow (location:scroll): re-anchor to the target every frame so a
  // trans/o2p/rotate/scale running on it carries the video along. Placement
  // above is a one-off read of the target's rect; without this the target
  // animates away and the video stays where it spawned.
  const follow = p.follow === "1" || p.follow === 1 || p.follow === true;
  if (follow && p.location === "scroll" && baseEl && !isFsSize && !centerOnViewport) {
    const place = stageParent
      ? () => {
          const a = anchorPoint();
          if (!a) return;
          vid.style.left = `${a.x - dispW / 2}px`;
          vid.style.top = `${a.y - dispH / 2}px`;
        }
      : vid._scrollHandler;
    let raf = 0;
    const tick = () => {
      // A display:none target (hidden panel, page teardown) has no client
      // rects — hold the last position rather than jumping to the origin.
      if (baseEl.isConnected && baseEl.getClientRects().length) place?.();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    vid._followPlace = place;
    vid._followStop = () => cancelAnimationFrame(raf);
  }

  // --- Handle video timing, fades, and in/out offsets
  vid.style.visibility = "hidden"; // hide the element to prevent the first frame flash

  // --- Grain scheduler (grainMode only). loop:N counts grains, loop:0 = endless.
  function scheduleGrainEnd() {
    if (!grainMode || !(seg > 0)) return;   // no seg: grains end at out:/the file end
    clearTimeout(vid._grainTimer);
    vid._grainTimer = setTimeout(nextGrain, (seg / (rate || 1)) * 1000);
  }
  function nextGrain() {
    grains++;
    if (!loopInfinite && grains >= maxLoops) { removeVideo(); return; }
    inTime = drawNext(inSpec, inTime);
    seg = drawNext(segSpec, seg);
    rate = Math.abs(drawNext(speedSpec, rate)) || 1;
    vid.playbackRate = rate;
    vid.currentTime = inTime;
    vid.play().catch(() => { /* autoplay blocked or play interrupted by pause */ });
    scheduleGrainEnd();
  }

  // its fade in, and its fade out before out: (as a web animation that
  // holds its end, and a timer kept so a re-trigger can cancel both)
  function startFades() {
    if (fadeInDur > 0) {
      vid.style.opacity = 0;
      vid.animate([{ opacity: 0 }, { opacity: opacityTarget }], {
        duration: fadeInDur * 1000,
        fill: "forwards",
        easing: "ease-out",
      });
    } else {
      vid.style.opacity = opacityTarget;
    }
    if (outTime > 0) {
      // started before out: so the fade is over by then
      const fadeOutStart = Math.max((outTime - fadeOutDur) * 1000, 0);
      vid._outFade = setTimeout(() => {
        vid.animate([{ opacity: opacityTarget }, { opacity: 0 }], {
          duration: fadeOutDur * 1000,
          fill: "forwards",
          easing: "ease-in",
        });
      }, fadeOutStart);
    }
  }

  vid.addEventListener("loadedmetadata", () => {

    //  SEEK TO START ("in:" parameter)

    if (inTime > 0) {
      vid.pause(); // stop autoplay so we can seek safely
      vid.currentTime = inTime; // jump to desired start position in seconds

      // Wait until the seek completes before revealing or playing
      vid.addEventListener(
        "seeked",
        () => {
          vid.style.visibility = "visible"; // show the video only after the seek is complete
          vid.play(); // now begin playback from the desired inTime
          scheduleGrainEnd();

          startFades();
        },
        { once: true } // ensures the event handler runs only once
      );
    }

    //  NO "in:" PARAMETER — PLAY IMMEDIATELY
    // --------------------------
    else {
      vid.style.visibility = "visible"; // show right away
      vid.play();
      scheduleGrainEnd();

      startFades();
    }
  });


  // --- Looping & removal
  if (grainMode) {
    // a grain that runs into the file end (or has no seg:) ends there → next grain
    vid.addEventListener("ended", nextGrain);
  } else if (loopInfinite) {
    vid.loop = true;
  } else if (maxLoops > 1) {
    vid.addEventListener("ended", () => {
      loopCount++;
      if (loopCount < maxLoops) {
        vid.currentTime = inTime;
        vid.play();
      } else removeVideo();
    });
  } else {
    vid.addEventListener("ended", removeVideo);
  }

  if (totalHold && totalHold > 0) {
    // hold + fadeOut: fade gracefully so removal isn't an abrupt pop
    if (fadeOutDur > 0) {
      const fadeStart = Math.max(totalHold - fadeOutDur * 1000, 0);
      setTimeout(() => {
        vid.animate([{ opacity: opacityTarget }, { opacity: 0 }], {
          duration: fadeOutDur * 1000,
          fill: "forwards",
          easing: "ease-in",
        });
      }, fadeStart);
    }
    setTimeout(removeVideo, totalHold);
  }

  if (!layerBack) vid.addEventListener("click", removeVideo);
}
